import { supabase } from './supabase'
import type { Trip, Stop, TripCalculation, RouteSegment } from '@/types'

export class TripService {
  async createTrip(name: string, userId: string, vehicleId?: string): Promise<Trip> {
    console.log('🌐 TripService.createTrip called')
    console.log('🌐 name:', name)
    console.log('🌐 userId:', userId)
    console.log('🌐 vehicleId:', vehicleId)
    
    const trip = {
      name,
      user_id: userId,
      vehicle_id: vehicleId || null,
      total_distance: 0,
      total_duration: 0,
      total_cost: 0,
      fuel_consumption: 8.0, // Default L/100km (will be updated from vehicle)
      fuel_price: 1.5, // Default price per liter
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    console.log('🌐 Inserting trip:', trip)

    const { data, error } = await supabase
      .from('trips')
      .insert(trip)
      .select()
      .single()

    if (error) {
      console.error('❌ Supabase createTrip error:', error)
      console.error('❌ Error code:', error.code)
      console.error('❌ Error message:', error.message)
      console.error('❌ Error details:', error.details)
      console.error('❌ Error hint:', error.hint)
      throw error
    }

    console.log('✅ Trip created:', data)
    return this.mapTripFromDB(data)
  }

  async getTrips(userId: string): Promise<Trip[]> {
    const { data, error } = await supabase
      .from('trips')
      .select(`
        *,
        stops (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error

    return data.map(trip => this.mapTripFromDB(trip))
  }

  async getTripById(id: string): Promise<Trip | null> {
    const { data, error } = await supabase
      .from('trips')
      .select(`
        *,
        stops (*)
      `)
      .eq('id', id)
      .single()

    if (error) {
      if (error.code === 'PGRST116') return null
      throw error
    }

    return this.mapTripFromDB(data)
  }

  async updateTrip(id: string, updates: Partial<Trip>): Promise<void> {
    const updateData: any = {
      updated_at: new Date().toISOString()
    }

    if (updates.name !== undefined) updateData.name = updates.name
    if (updates.totalDistance !== undefined) updateData.total_distance = updates.totalDistance
    if (updates.totalDuration !== undefined) updateData.total_duration = updates.totalDuration
    if (updates.totalCost !== undefined) updateData.total_cost = updates.totalCost
    if (updates.fuelConsumption !== undefined) updateData.fuel_consumption = updates.fuelConsumption
    if (updates.fuelPrice !== undefined) updateData.fuel_price = updates.fuelPrice
    if (updates.vehicleId !== undefined) updateData.vehicle_id = updates.vehicleId

    const { error } = await supabase
      .from('trips')
      .update(updateData)
      .eq('id', id)

    if (error) throw error
  }

  async deleteTrip(id: string): Promise<void> {
    const { error } = await supabase
      .from('trips')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  async addStop(tripId: string, stop: Omit<Stop, 'id' | 'createdAt' | 'updatedAt'>): Promise<Stop> {
    console.log('🌐 tripService.addStop called')
    console.log('🌐 tripId:', tripId)
    console.log('🌐 stop:', stop)
    
    const stopData = {
      trip_id: tripId,
      name: stop.name,
      address: stop.address,
      type: stop.type,
      duration: stop.duration,
      lat: stop.lat,
      lng: stop.lng,
      order: stop.order,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    console.log('🌐 stopData to insert:', stopData)

    const { data, error } = await supabase
      .from('stops')
      .insert(stopData)
      .select()
      .single()

    if (error) {
      console.error('❌ Supabase addStop error:', error)
      console.error('❌ Error code:', error.code)
      console.error('❌ Error message:', error.message)
      console.error('❌ Error details:', error.details)
      console.error('❌ Error hint:', error.hint)
      throw error
    }

    console.log('✅ Supabase response:', data)

    return this.mapStopFromDB(data)
  }

  async updateStop(id: string, updates: Partial<Stop>): Promise<void> {
    const { error } = await supabase
      .from('stops')
      .update({
        name: updates.name,
        address: updates.address,
        type: updates.type,
        duration: updates.duration,
        lat: updates.lat,
        lng: updates.lng,
        order: updates.order,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)

    if (error) throw error
  }

  async deleteStop(id: string): Promise<void> {
    const { error } = await supabase
      .from('stops')
      .delete()
      .eq('id', id)

    if (error) throw error
  }

  async calculateRoute(
    stops: Stop[],
    fuelConsumption: number = 8.0,
    fuelPrice: number = 1.5
  ): Promise<TripCalculation> {
    // This would integrate with a routing service like OSRM or Mapbox
    // For now, we'll use a simplified calculation
    const segments: RouteSegment[] = []
    let totalDistance = 0
    let totalDuration = 0

    for (let i = 0; i < stops.length - 1; i++) {
      const start = stops[i]
      const end = stops[i + 1]
      
      // Simple distance calculation (Haversine formula) - returns meters
      const distance = this.calculateDistance(
        start.lat, start.lng,
        end.lat, end.lng
      )
      
      const duration = (distance / 1000 / 80) * 60 // distance in meters, convert to km, then calculate duration at 80 km/h
      
      segments.push({
        distance,
        duration,
        coordinates: [[start.lng, start.lat], [end.lng, end.lat]]
      })

      totalDistance += distance
      totalDuration += duration
    }

    // Add stop durations
    const stopDuration = stops.reduce((sum, stop) => sum + stop.duration, 0)
    totalDuration += stopDuration

    // Calculate fuel cost using provided values
    const totalFuelCost = (totalDistance / 1000 / 100) * fuelConsumption * fuelPrice

    return {
      totalDistance, // in meters
      totalDuration, // in minutes
      totalFuelCost, // in EUR (or user's currency)
      segments
    }
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    // Haversine formula - returns distance in METERS
    const R = 6371000 // Earth's radius in meters (was 6371 km)
    const dLat = this.toRad(lat2 - lat1)
    const dLng = this.toRad(lng2 - lng1)
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c // Returns meters
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180)
  }

  private mapTripFromDB(data: any): Trip {
    return {
      id: data.id,
      name: data.name,
      userId: data.user_id,
      vehicleId: data.vehicle_id,
      stops: data.stops ? data.stops.map((stop: any) => this.mapStopFromDB(stop)) : [],
      totalDistance: data.total_distance || 0,
      totalDuration: data.total_duration || 0,
      totalCost: data.total_cost || 0,
      fuelConsumption: data.fuel_consumption || 8.0,
      fuelPrice: data.fuel_price || 1.5,
      status: data.status || 'active',
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    }
  }

  private mapStopFromDB(data: any): Stop {
    return {
      id: data.id,
      name: data.name,
      address: data.address,
      type: data.type,
      duration: data.duration,
      lat: data.lat,
      lng: data.lng,
      order: data.order,
      createdAt: new Date(data.created_at),
      updatedAt: new Date(data.updated_at)
    }
  }
}

export const tripService = new TripService()